import api from "@/lib/axios"

export interface SkillVideo {
    url: string;
    caption: string;
}

export interface SkillStats {
    id: string;
    name: string;
    domainName?: string;
    totalQuestions: number;
    videoLinks: SkillVideo[];
}

export interface QuestionStats {
    total: number;
    pending_review: number;
    by_domain: { name: string; count: number }[];
    by_difficulty: { level: string; count: number }[];
    by_status: { status: string; count: number }[];
    by_provider: { name: string; count: number }[];
}

export interface LeaderboardEntry {
    reviewer_id: number;
    name: string;
    points: number;
    approved_count: number;
    pending_count: number;
    total_assigned: number;
}

export interface CreatorLeaderboardEntry {
    creator_id: number;
    name: string;
    count: number;
}

export interface TutorialUploaderLeaderboardEntry {
    uploader_id: number;
    name: string;
    count: number;
}

export interface IssueResolverEntry {
    reviewer_id: number;
    name: string;
    total_resolved: number;
    by_issue: Record<string, number>;
    last_approved_at?: string | null;
}

export interface IssueResolversData {
    issue_types: string[];
    resolvers: IssueResolverEntry[];
}

export interface ImposterSearchCriteria {
    stimulusMin?: number;
    stimulusMax?: number;
    stemMin?: number;
    stemMax?: number;
    explanationMin?: number;
    explanationMax?: number;
    optionsMin?: number;

    // Null/Empty checks
    stimulusMissing?: boolean;
    stemMissing?: boolean;
    explanationMissing?: boolean;
    optionsMissing?: boolean;

    // Filters
    questionCategory?: string;
}

export interface QuestionData {
    identifier?: string // Added
    usages?: string[] // Added for dual save (Practice, Mock)
    stimulus?: string
    stem: string
    choices?: Record<string, string>
    mcq_options?: Record<string, string>
    mcq_answers?: string
    spr_answers?: Array<string | number>
    correct_answer: string
    explanation?: string
    question_category: string
    question_type?: 'mcq' | 'spr'
    status?: string
    domain: string
    domain_name?: string
    skill: string
    skill_name?: string
    difficulty: string
    source?: string
    tutorial_link?: string
    voice_note_url?: string
    solution_images?: { id: string; url: string; caption: string; order: number }[]
    review_issues?: string[]
    is_pending_review?: boolean
    reviewer_id?: number | null
    reviewerName?: string
    provider_name?: string
    provider_code?: string
}

export interface QuestionResponse {
    id: string | number
    questionId: string
    identifier?: string // Added
    usages?: string[] // Added
    stimulus?: string
    stem?: string
    mcqOptions?: Record<string, string>
    mcqAnswer?: string
    sprAnswers?: Array<string>
    correctAnswer?: string
    questionCategory?: string
    questionType?: 'mcq' | 'spr'
    domainName?: string
    skillName?: string
    difficulty?: string
    tutorialLink?: string
    explanation?: string
    voiceNoteUrl?: string
    solutionImages?: { id: string; url: string; caption: string; order: number }[]
    status?: string; // Added for status toggle feature
    reviewIssues?: string[]
    reviewStatus?: string; // Added for reported questions
    reportReason?: string; // Added for reported questions
    isPendingReview?: boolean
    reviewerId?: number
    reviewerName?: string
    providerName?: string
    providerCode?: string
    createdById?: number
    createdByEmail?: string
    createdByName?: string
    // Tutorial upload tracking
    tutorialUploadedById?: number
    tutorialUploadedByEmail?: string
    tutorialUploadedByName?: string
    tutorialUploadedAt?: string
    // Voice note upload tracking
    voiceNoteUploadedById?: number
    voiceNoteUploadedByEmail?: string
    voiceNoteUploadedByName?: string
    voiceNoteUploadedAt?: string
    singleTestLink?: {
        templateId: number;
        moduleIndex: number;
    }
}

export interface SourceFilter {
    code: string;
    bluebook_included?: boolean;
}

export const adminQuestionsService = {
    getStats: async (): Promise<QuestionStats> => {
        const response = await api.get("/admin/questions/stats")
        return response.data
    },
    getLeaderboard: async (): Promise<LeaderboardEntry[]> => {
        const response = await api.get("/admin/questions/stats/leaderboard")
        return response.data
    },
    getCreatorLeaderboard: async (): Promise<CreatorLeaderboardEntry[]> => {
        const response = await api.get("/admin/questions/stats/leaderboard/creators")
        return response.data
    },
    getTutorialUploaderLeaderboard: async (): Promise<TutorialUploaderLeaderboardEntry[]> => {
        const response = await api.get("/admin/questions/stats/leaderboard/tutorials")
        return response.data
    },
    getIssueResolvers: async (): Promise<IssueResolversData> => {
        const response = await api.get("/admin/questions/stats/issue-resolvers")
        return response.data
    },
    getAllQuestions: async (params: { page: number; limit: number; search?: string; domain?: string; difficulty?: string; status?: string; question_category?: string; question_type?: string; created_by_id?: number; reviewer_id?: number; tutorial_uploaded_by_id?: number }) => {
        const response = await api.get("/admin/questions", { params })
        return response.data
    },
    createQuestion: async (data: Partial<QuestionData>) => {
        const response = await api.post("/admin/questions/", data)
        return response.data
    },
    getReviewStats: async (): Promise<{ remaining: number; approved: number; points: number }> => {
        const response = await api.get("/admin/questions/review/stats/me")
        return response.data
    },
    getReviewIssueTypes: async (): Promise<string[]> => {
        const response = await api.get("/admin/questions/review/issues/types")
        return response.data
    },
    getReviewQuestions: async (params: { page: number; limit: number; domain?: string; difficulty?: string; search?: string; provider?: string; source?: string; review_issues?: string[] }) => {
        const response = await api.get("/admin/questions/review", { params })
        return response.data
    },
    getMostReportedQuestions: async (params: { page: number; limit: number; domain?: string; difficulty?: string; search?: string; provider?: string }) => {
        const response = await api.get("/admin/questions/review/analysis/most-reported", { params })
        return response.data
    },
    claimQuestion: async (questionId: string) => {
        const response = await api.post(`/admin/questions/review/${questionId}/claim`)
        return response.data
    },
    unclaimQuestion: async (questionId: string) => {
        const response = await api.post(`/admin/questions/review/${questionId}/unclaim`)
        return response.data
    },
    returnAllQuestions: async () => {
        const response = await api.post("/admin/questions/review/unclaim-all")
        return response.data
    },
    claimBulkQuestions: async (params?: { domain?: string; difficulty?: string; search?: string; review_issues?: string[] }) => {
        const response = await api.post("/admin/questions/review/claim-bulk", null, { params })
        return response.data
    },
    getMyReviewQuestions: async (params: { page: number; limit: number; domain?: string; difficulty?: string; search?: string; provider?: string }) => {
        const response = await api.get("/admin/questions/review/my-reviews", { params })
        return response.data
    },
    getMyApprovedQuestions: async (params: { page: number; limit: number; domain?: string; difficulty?: string; search?: string; provider?: string }) => {
        const response = await api.get("/admin/questions/review/my-approved", { params })
        return response.data
    },
    reopenQuestion: async (questionId: string) => {
        const response = await api.post(`/admin/questions/review/${questionId}/reopen`)
        return response.data
    },
    getQuestion: async (questionId: string): Promise<QuestionResponse> => {
        const response = await api.get(`/admin/questions/${questionId}`)
        return response.data
    },
    updateQuestion: async (questionId: string, data: Partial<QuestionData>) => {
        const response = await api.put(`/admin/questions/${questionId}`, data)
        return response.data
    },
    deleteQuestion: async (questionId: string) => {
        const response = await api.delete(`/admin/questions/${questionId}`)
        return response.data
    },
    searchImposterQuestions: async (criteria: ImposterSearchCriteria) => {
        const response = await api.post("/admin/questions/search/imposter", criteria)
        return response.data
    },
    getAdjacentQuestions: async (questionId: string) => {
        const response = await api.get(`/admin/questions/${questionId}/adjacent`)
        return response.data
    },
    getProviders: async (): Promise<{ code: string; name: string }[]> => {
        const response = await api.get("/admin/questions/providers")
        return response.data
    },
    getIdentifiers: async (): Promise<string[]> => {
        const response = await api.get("/admin/questions/identifiers")
        return response.data
    },
    getDynamicSkills: async (): Promise<Record<string, string[]>> => {
        const response = await api.get("/admin/questions/dynamic-skills")
        return response.data
    },
    uploadVideo: async (file: File | Blob, onProgress?: (pct: number) => void): Promise<{ url: string }> => {
        const formData = new FormData()
        const filename = file instanceof File ? file.name : "tutorial.webm"
        formData.append("file", file, filename)
        const response = await api.post("/upload/spaces/video", formData, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: onProgress
                ? (evt) => {
                      if (evt.total) {
                          onProgress(Math.round((evt.loaded * 100) / evt.total))
                      }
                  }
                : undefined,
        })
        return response.data
    },
    getSkills: async (): Promise<SkillStats[]> => {
        const response = await api.get("/practice/skills")
        return response.data
    },
    updateSkillVideos: async (skillSlug: string, videos: SkillVideo[]): Promise<SkillStats> => {
        const response = await api.post(`/practice/skills/${skillSlug}/videos`, { video_links: videos })
        return response.data
    },
    transferToMock: async (questionId: string) => {
        const response = await api.post("/admin/mock-tests/questions/transfer", { question_id: questionId })
        return response.data
    },
    bulkChangeProvider: async (
        sources: SourceFilter[],
        toProviderName: string,
        toProviderCode?: string,
        toBluebookIncluded?: boolean
    ) => {
        const response = await api.post("/admin/questions/providers/bulk-change", {
            sources,
            to_provider_name: toProviderName,
            to_provider_code: toProviderCode,
            to_bluebook_included: toBluebookIncluded
        })
        return response.data
    },
    getQuestionProviders: async (): Promise<{ code: string; name: string; count: number; bluebook_included: boolean | null }[]> => {
        const response = await api.get("/admin/questions/providers/questions-only")
        return response.data
    },
    bulkFixerPreview: async (filters: BulkFixerFilters, updates: BulkFixerUpdates) => {
        const response = await api.post("/admin/questions/bulk-fixer/preview", {
            filters,
            updates
        })
        return response.data
    },
    bulkFixerExecute: async (filters: BulkFixerFilters, updates: BulkFixerUpdates) => {
        const response = await api.post("/admin/questions/bulk-fixer/execute", {
            filters,
            updates
        })
        return response.data
    },
    getBulkFixerMetadata: async () => {
        const response = await api.get("/admin/questions/bulk-fixer/metadata")
        return response.data
    }
}

export interface BulkFixerFilters {
    provider_codes?: string[]
    domain_names?: string[]
    skill_names?: string[]
    difficulties?: string[]
    bluebook_included?: boolean
    question_category?: string
}

export interface BulkFixerUpdates {
    provider_code?: string
    provider_name?: string
    domain_name?: string
    skill_name?: string
    difficulty?: string
    bluebook_included?: boolean
}
